<template>
    <m-form ref="form" class="m-form-object">

        <template #content-template>
            <v-flex class="container-responsive">
                <m-block-form-fields required>
                    <template #title>
                        {{$t('modules.forms.m-form-object.blocks.general-information.title')}}
                    </template>

                    <e-input-object-name v-model="form.name"
                                         :label="$t('elements.inputs.e-input-object-name.label', {msg: objectType.title})"/>
                </m-block-form-fields>

                <m-block-form-object-address v-bind.sync="{form}" @determine:geolocation="determine" :loading="loading"/>

                <m-block-form-object-contacts v-bind.sync="{form}"/>
            </v-flex>
        </template>
    </m-form>
</template>

<script>
    import MForm from "@modules/forms/m-form";
    import MBlockFormObjectAddress from "@modules/blocks/form/concrete/m-block-form-object-address";
    import MBlockFormObjectContacts from "@modules/blocks/form/concrete/m-block-form-object-contacts";
    import MBlockFormButtons from "@modules/blocks/buttons/concrete/m-block-form-buttons";
    import MBlockFormFields from "@modules/blocks/form/m-block-form-fields";
    import {mapGetters, mapActions} from 'vuex';

    import {OBJECT_CONTACT_TYPES} from "@constants/common";
    import isEmpty from 'lodash/isEmpty'
    import pick from 'lodash/pick'
    import compact from 'lodash/compact'
    import dialogs from '@mixins/includes/_dialogs';
    import vuexKeys from '@mixins/includes/_vuexKeys'
    import notifications from "@mixins/includes/_notifications";

    const defaultContactData = () => (
        {
            type: OBJECT_CONTACT_TYPES[0],
            value: ''
        }
    );
    const defaultFormData = () => ({
        name: null,
        address: null,
        postcode: null,
        locality: null,
        contact: null,
        extraContact: null,
        geolocation: {
            latitude: null,
            longitude: null
        },
        phoneNumber: defaultContactData(),
        mobileNumber: defaultContactData(),
        email: defaultContactData(),
    });

    export default {
        name: "m-form-object",
        components: {
            MForm,
            MBlockFormObjectContacts,
            MBlockFormObjectAddress, MBlockFormFields, MBlockFormButtons
        },
        mixins: [vuexKeys, dialogs, notifications],
        $_veeValidate: {
            validator: 'new'
        },
        props: {
            objectTypeId: {
                type: String,
                required: true
            },
            object: {
                type: Object,
                default: null
            },
            isOnboarding: {
                type: Boolean,
                default: false
            }
        },
        data: () => ({
            form: defaultFormData(),
            loading: false
        }),
        created() {
            if (this.object) {
                if (isEmpty(this.object.phoneNumber)) {
                    Object.assign(this.form, {phoneNumber: defaultContactData()})
                }
                if (isEmpty(this.object.mobileNumber)) {
                    Object.assign(this.form, {mobileNumber: defaultContactData()})
                }
                if (isEmpty(this.object.email)) {
                    Object.assign(this.form, {email: defaultContactData()})
                }
            }
        },
        watch: {
            object: {
                immediate: true,
                handler() {
                    Object.assign(this.form, {
                        ...this.object
                    });
                }
            }
        },
        computed: {
            ...mapGetters({
                getObjectType: 'objectTypes/read'
            }),

            objectType() {
                return this.getObjectType(this.objectTypeId)
            },

            loaderKey() {
                return isEmpty(this.object) ? this.keys.objects.create : this.keys.objects.update + this.object._id
            },

            buttonsText() {
                return {
                    submit: this.$t('modules.forms.m-form-object.buttons.submit'),
                    cancel: this.$t('modules.forms.m-form-object.buttons.cancel')
                }
            }
        },
        methods: {
            ...mapActions({
                getAddress: 'geolocation/convert'
            }),
            isSomeAddrDataExists(fields = ['address', 'postcode', 'locality']) {
                return !isEmpty(compact(Object.values(pick(this.form, fields))))
            },

            submit() {
                this.$validator.validateAll().then((success) => {
                    if (success) {
                        let data = Object.assign({}, this.form);
                        this.normalizeContactsData(data);
                        this.$emit('form:submit', data);
                    } else {
                        const el = document.querySelector(".v-messages__message");
                        el.scrollIntoView({behavior: "smooth" ,block: "center"});
                        this.notify.error(this.$t('notifications.errors.formErrors.invalid'))
                    }
                });
            },

            cancel() {
                this.$emit('form:cancel', this.form);
            },

            async reset() {
                await this.$validator.reset();
                this.form = defaultFormData();
            },

            normalizeContactsData(data) {
                if (!data.phoneNumber.value) {
                    data.phoneNumber = null
                }
                if (!data.mobileNumber.value) {
                    data.mobileNumber = null
                }
                if (!data.email.value) {
                    data.email = null
                }
            },

            determine() {
                this.loading = true;
                if (navigator.geolocation) {
                    navigator.geolocation.getCurrentPosition(async (position) => {
                        if (this.isSomeAddrDataExists()) {
                            try {
                                await this.openDialog({type: 'overwritingConfirmation'})
                            } catch (e) {
                                this.loading = false;
                                return;
                            }
                        }

                        this.openDialog({
                            type: 'geolocationMap',
                            options: {
                                lat: position.coords.latitude,
                                lng: position.coords.longitude
                            }
                        }).then((geolocation) => {
                            this.form.geolocation = geolocation;
                            this.requestAddress();
                            this.loading = false;
                        });

                    }, (error) => {
                        switch (error.code) {
                            case 1:
                                this.notify.infoText(this.$t('notifications.errors.geolocation.rejected'));
                                break;
                            case 2:
                                this.notify.error(this.$t('notifications.errors.geolocation.networkError'));
                                break;
                            case 3:
                                this.notify.error(this.$t('notifications.errors.geolocation.timeout'));
                                break;
                            default:
                                this.notify.error(this.$t('notifications.errors.geolocation.unknown'));
                                break;
                        }
                        this.openDialog({
                            type: 'geolocationMap',
                            options: {
                                lat: 52.51859614944326,
                                lng: 13.394558262500004
                            }
                        }).then((geolocation) => {
                            this.form.geolocation = geolocation;
                            this.requestAddress();
                            this.loading = false;
                        });
                    });
                } else {
                    this.notify.error(this.$t('notifications.errors.geolocation.notSupport'));
                    this.loading = false;
                }
            },
            async requestAddress() {
                const res = await this.getAddress({
                    lat: this.form.geolocation.latitude,
                    lon: this.form.geolocation.longitude
                });

                Object.assign(this.form, {
                    address: res.strasse,
                    postcode: res.postleitzahl,
                    locality: res.ort
                });
            }
        }
    }
</script>

<style lang="scss">
    .container-responsive {
        @include media(md) {
            padding-left: 0 !important;
        }
    }
    .m-form-object {
        padding: 0 0 0 1rem !important;

        @media (max-width: $md) {
            padding: 0 !important;
        }

        .v-divider {
            display: none;
        }
    }
    .fullscreen {
        .m-form-object-types {
            padding: 0 !important;
        }
    }
    .fullscreen {
        .m-form-object {
            padding: 0 !important;
        }
        .container-responsive {
            padding: 0 !important;
        }
    }
</style>
